function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
// @ts-ignore
import packageJson from "../../package.json";
import { setReqRetryInterceptor, setResRetryInterceptor } from "../interceptors/features/retry";
import { setReqSentryPerformanceMonitoring, setResSentryPerformanceMonitoring } from "../interceptors/features/tracing";
import { setResGraphQLError } from "../interceptors/response/resGraphqlError";
import { createAxiosInstance } from "./axios/instance";
import { setGraphQLCompression, setGraphQLFile } from "./utils/graphql";
var DEFAULT_TIMEOUT = 10000; // ten seconds in milliseconds
/**
 * HttpClient is the standardized API client for all of our services.
 * It is a wrapper around Axios with built-in interceptors.
 * @class HttpClient
 */ var HttpClient = /*#__PURE__*/ function() {
    "use strict";
    function HttpClient(param) {
        var apiName = param.apiName, clientName = param.clientName, baseUrl = param.baseUrl, headers = param.headers, userAgent = param.userAgent, _param_throwGraphQLErrors = param.throwGraphQLErrors, throwGraphQLErrors = _param_throwGraphQLErrors === void 0 ? false : _param_throwGraphQLErrors, _param_options = param.options, options = _param_options === void 0 ? {} : _param_options;
        this.defaultTimeout = options.timeout || DEFAULT_TIMEOUT;
        this.clientName = clientName;
        this.retryInterceptor = {
            enabled: false
        };
        this.throwGraphQLErrors = throwGraphQLErrors;
        this.axios = createAxiosInstance({
            headers: headers,
            clientName: clientName,
            apiName: apiName,
            userAgent: userAgent,
            baseUrl: baseUrl,
            options: {
                timeout: this.defaultTimeout
            }
        });
        if (throwGraphQLErrors) {
            setResGraphQLError(this);
        }
    }
    var _proto = HttpClient.prototype;
    /**
   * This method allows applications to set the BaseURL for the HttpClient before making requests.
   * It empowers teams to create the HttpClient with a default URL and then override it as needed.
   * @param baseUrl the base URL for the HttpClient. (e.g. "https://shop.lululemon.com")
   */ _proto.setBaseUrl = function setBaseUrl(baseUrl) {
        this.axios.defaults.baseURL = baseUrl;
    };
    /**
   * This method allows applications to set the UserAgent for the HttpClient before making requests.
   * The UserAgent is a string that identifies the client making the request.
   * It is used by servers to identify the client making the request.
   * We enforce using the format "lululemon/{clientName}". It is added automatically.
   * @param userAgent The user-agent to be used (without the lululemon prefix).
   */ _proto.setUserAgent = function setUserAgent(userAgent) {
        this.axios.defaults.headers["user-agent"] = "lululemon/" + userAgent;
    };
    /**
   * Enables Performance Monitoring using Sentry.
   * This creates a Sentry Transaction for each request and attaches it to the axios request instance.
   * Those transactions include contextual information about the request and response, as well as performance metrics.
   */ _proto.enablePerformanceMonitoring = function enablePerformanceMonitoring() {
        setReqSentryPerformanceMonitoring(this);
        setResSentryPerformanceMonitoring(this);
    };
    /**
   * Enables retry logic for the HttpClient.
   * Receives a list of status codes to retry on, a limit of retries, and an optional header (overrider) to track the number of retries.
   * Also allows for enabling retrying on timeouts, and setting a delay between each retry.
   * @param {RetryOptions} retryOptions - The options for the retry logic.
   */ _proto.enableRetry = function enableRetry(retryOptions) {
        if (this.retryInterceptor.enabled) {
            this.disableRetry();
        }
        var reqRetryInterceptor = setReqRetryInterceptor(this, retryOptions);
        var resRetryInterceptor = setResRetryInterceptor(this, retryOptions);
        this.retryInterceptor = {
            enabled: true,
            req: reqRetryInterceptor,
            res: resRetryInterceptor,
            options: retryOptions
        };
    };
    /**
   * Disables the retry logic for the HttpClient.
   * It will remove the request and response interceptors for retrying requests.
   * It will also log a warning if the retry interceptor is not enabled.
   */ _proto.disableRetry = function disableRetry() {
        if (this.retryInterceptor.enabled) {
            this.axios.interceptors.request.eject(this.retryInterceptor.req);
            this.axios.interceptors.response.eject(this.retryInterceptor.res);
            this.retryInterceptor = {
                enabled: false
            };
        } else {
            console.warn("Retry Interceptor not enabled. Skipping ejection.");
        }
    };
    /**
   * Internally calls the Axios request method.
   * Used so we can generate custom methods like the graphql method.
   * @param {AxiosRequestConfig} reqOptions - The options for the request, consult Axios docs.
   */ _proto.request = function request(reqOptions) {
        return this.axios.request(reqOptions);
    };
    _proto.post = function post(url, requestOptions) {
        if (requestOptions === void 0) requestOptions = {};
        return this.request(_extends({}, requestOptions, {
            url: url,
            method: "post"
        }));
    };
    _proto.patch = function patch(url, requestOptions) {
        if (requestOptions === void 0) requestOptions = {};
        return this.request(_extends({}, requestOptions, {
            url: url,
            method: "patch"
        }));
    };
    _proto.get = function get(url, requestOptions) {
        if (requestOptions === void 0) requestOptions = {};
        return this.request(_extends({}, requestOptions, {
            url: url,
            method: "get"
        }));
    };
    _proto.delete = function _delete(url, requestOptions) {
        if (requestOptions === void 0) requestOptions = {};
        return this.request(_extends({}, requestOptions, {
            url: url,
            method: "delete"
        }));
    };
    /**
   * Custom method to make a GraphQL request.
   * It includes helper methods to accept a GraphQL document file as the query object.
   * It also will compress the query body to reduce the size of the request by removing whitespace and line breaks.
   * @param {GraphQLRequest} requestOptions - Enhanced AxiosRequestConfig with a query and variables on the body.
   */ _proto.graphql = function graphql(requestOptions) {
        setGraphQLFile(requestOptions);
        setGraphQLCompression(requestOptions);
        return this.request(_extends({}, requestOptions, {
            method: "post"
        }));
    };
    return HttpClient;
}();
(function() {
    HttpClient.clientVersion = packageJson.version // the version of the mwa-http-client package
    ;
})();
export default HttpClient;
